import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Collapse, Container, Row, UncontrolledTooltip } from 'reactstrap';
import { LanguageCode } from '@payer-admin-ui/api/src/invoices/models/LanguageCode';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { RadioButtonGroup } from '../../RadioButton/RadioButtonGroup';
import { CountryDropdown } from '../../Dropdowns/CountryDropdown';
import { LanguageDropdown } from '../../Dropdowns/LanguageDropdown';
import { ExpandableContainer } from '../../ExpandableContainer/ExpandableContainer';
import { TextInput } from '../../Input/TextInput';
import { RegNumberField } from '../DetailedInvoiceCard/RegNumberField';
import styles from './UpdateInvoiceCustomerCard.module.scss';

const UpdateInvoiceCustomerCard = ({ customer, patchCustomer, deliveryType, expanded }) => {
  const { t } = useTranslation();

  const {
    customerNumber,
    companyName,
    customerType = companyName !== null ? 'ORGANISATION' : 'PRIVATE',
    address,
    regNumber,
    customerVatId,
  } = customer ?? {};

  const isEInvoice = deliveryType === DeliveryType.EINVOICE;
  const regNumberOrCustomerVatIdIsSpecified = regNumber || customerVatId;

  return (
    <ExpandableContainer
      header={<b>{t('components.cards.invoice.customer.header')}</b>}
      defaultExpanded={expanded ?? true}
    >
      {customer && (
        <Container className={styles.UpdateInvoiceCustomerCard}>
          <Row className={styles.section}>
            <Col>
              <RadioButtonGroup
                items={t('enums.customerType.values', { returnObjects: true })}
                title={<h5>{t('components.cards.invoice.customer.type')}</h5>}
                selectedItem={customerType}
                inline
                disabled
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="6">
              <h5>{t('components.cards.invoice.customer.country')}</h5>
              <CountryDropdown
                selectedCountry={address?.countryCode}
                setSelectedCountry={countryCode => patchCustomer({ address: { countryCode } })}
                validators={[Validators.notEmpty]}
                disabled
              />
            </Col>
            <Col xs="6">
              <h5>{t('components.cards.invoice.customer.language')}</h5>
              <LanguageDropdown
                languages={[
                  LanguageCode.da,
                  LanguageCode.nl,
                  LanguageCode.en,
                  LanguageCode.fr,
                  LanguageCode.de,
                  LanguageCode.es,
                  LanguageCode.sv,
                ]}
                selectedLanguage={address?.languageCode}
                setSelectedLanguage={languageCode => patchCustomer({ address: { languageCode } })}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col>
              <TextInput
                title={<h5>{t('invoices.fields.customerNumber')}</h5>}
                value={customerNumber}
                setValue={customerNumber => patchCustomer({ customerNumber })}
                validators={[Validators.length(0, 48)]}
              />
            </Col>
          </Row>
          <Row className={styles.section} />
          <Row>
            <Col xs="12" sm="6">
              <RegNumberField
                countryCode={address?.countryCode}
                value={regNumber}
                setValue={regNumber => patchCustomer({ regNumber })}
                deliveryType={deliveryType}
                customerVatId={customerVatId}
                addressFetchState={{ isFetching: false }}
              />
            </Col>
            <Col xs="12" sm="6">
              <Container className="p-0 m-0">
                <Row>
                  <Col>
                    <h5>{t('invoices.fields.vatId')}</h5>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextInput
                      value={customerVatId}
                      setValue={customerVatId => patchCustomer({ customerVatId })}
                      validators={[
                        ...(regNumberOrCustomerVatIdIsSpecified ? [] : [Validators.notEmpty]),
                        Validators.length(0, 48),
                      ]}
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
          <Row className={styles.section} />
          <Row className={styles.section}>
            <Col xs="12">
              <h5>{t('components.cards.invoice.customer.invoiceAddress')}</h5>
            </Col>
            <Col xs="12" sm="6">
              <Collapse isOpen={customerType === 'ORGANISATION'}>
                <TextInput
                  title={t('address.companyName')}
                  value={address?.companyName}
                  setValue={companyName => patchCustomer({ address: { companyName } })}
                  validators={[
                    ...(customerType === 'ORGANISATION' ? [Validators.notEmpty] : []),
                    Validators.length(0, 128),
                  ]}
                />
              </Collapse>
            </Col>
            <Col xs="12" sm="6">
              <Collapse isOpen={customerType === 'ORGANISATION'}>
                <TextInput
                  title={t('address.yourReference')}
                  value={address?.yourReference}
                  setValue={yourReference => patchCustomer({ address: { yourReference } })}
                  validators={[
                    ...(customerType === 'ORGANISATION' ? [Validators.notEmpty] : []),
                    Validators.length(0, 48),
                  ]}
                />
              </Collapse>
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.firstName')}
                value={address?.firstName}
                setValue={firstName => patchCustomer({ address: { firstName } })}
                validators={[
                  ...(customerType !== 'ORGANISATION' ? [Validators.notEmpty] : []),
                  Validators.length(0, 48),
                ]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.lastName')}
                value={address?.lastName}
                setValue={lastName => patchCustomer({ address: { lastName } })}
                validators={[
                  ...(customerType !== 'ORGANISATION' ? [Validators.notEmpty] : []),
                  Validators.length(0, 48),
                ]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.streetAddress1')}
                value={address?.streetAddress1}
                setValue={streetAddress1 => patchCustomer({ address: { streetAddress1 } })}
                validators={[Validators.notEmpty, Validators.length(0, 128)]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.streetAddress2')}
                value={address?.streetAddress2}
                setValue={streetAddress2 => patchCustomer({ address: { streetAddress2 } })}
                validators={[Validators.length(0, 128)]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.zipCode')}
                value={address?.zipCode}
                setValue={zipCode => patchCustomer({ address: { zipCode } })}
                validators={[Validators.notEmpty, Validators.length(0, 20)]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.city')}
                value={address?.city}
                setValue={city => patchCustomer({ address: { city } })}
                validators={[Validators.notEmpty, Validators.length(0, 48)]}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.emailAddress')}
                value={address?.emailAddress}
                setValue={emailAddress => patchCustomer({ address: { emailAddress } })}
                validators={[Validators.validIfEmpty, Validators.length(0, 128), Validators.email]}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('address.phoneNumber')}
                value={address?.phoneNumber}
                setValue={phoneNumber => patchCustomer({ address: { phoneNumber: phoneNumber || null } })}
                validators={[Validators.length(0, 128)]}
              />
            </Col>
          </Row>
          {isEInvoice && (
            <Row className={styles.section}>
              <Col xs="12" sm="6">
                <div id="gln-input-field">
                  <TextInput
                    title={`${t('invoices.fields.eInvoice')} ${t('address.gln')}`}
                    value={address?.gln}
                    setValue={gln => patchCustomer({ address: { gln } })}
                    validators={[Validators.validIfEmpty, Validators.length(0, 128), Validators.number]}
                  />
                  <UncontrolledTooltip placement="top" target="gln-input-field" trigger="hover" delay={800}>
                    {t('invoices.mouseHovers.gln')}
                  </UncontrolledTooltip>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      )}
    </ExpandableContainer>
  );
};

UpdateInvoiceCustomerCard.propTypes = {
  customer: PropTypes.shape({
    companyName: PropTypes.string,
    address: PropTypes.object,
    regNumber: PropTypes.string,
  }),
  patchCustomer: PropTypes.func,
  deliveryType: PropTypes.string,
  expanded: PropTypes.bool,
};

export { UpdateInvoiceCustomerCard };
